import { gql } from "lib/apolloClient";

import {
  announcementFeedFragment,
  unreadAnnouncementsCountCommonFragment,
  unreadAnnouncementsCountFragment,
  userCourseDetailsFragment,
  manageMembersFragments,
} from "./AnnouncementFragments";
import {
  academicYearFragment,
  attachmentFragment,
  studentsAlertFragment,
} from "modules/CommonFragments";

export const getAnnouncementPublishersQuery = gql`
  query getAnnouncementPublishers(
    $organizationId: ID!
    $publishersInput: CircularPublishersInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        circularPublishers(input: $publishersInput) {
          id
          email
          firstName
          middleName
          lastName
          profileImage
          type
          displayRole
          preferredName
          pronouns
          prefix
          suffix
        }
      }
    }
  }
`;

export const getAnnouncementDetailsForTabQuery = gql`
  query getAnnouncementDetailsForTab($id: ID!, $academicYearId: ID!) {
    node(id: $id, type: CIRCULAR) {
      id
      ... on Circular {
        state
        createdBy {
          id
        }
        createdIn
        isSharedWithMe(input: { academicYearId: $academicYearId })
      }
    }
  }
`;

export const getAnnouncementDetailsQuery = gql`
  query getAnnouncementDetails(
    $id: ID!
    $academicYearId: ID!
    $isRecipientsVisible: Boolean!
    $getEntities: Boolean!
    $getReviewComment: Boolean!
  ) {
    node(id: $id, type: CIRCULAR) {
      id
      ... on Circular {
        ...announcementItem
        createdIn
        bannerV2 {
          id
          url
          name
          type
          mimeType
          metadata
        }
        createdAt
        sendEmail
        reviewComment @include(if: $getReviewComment) {
          id
          message
          createdBy {
            id
            firstName
            middleName
            lastName
          }
        }
        attachments {
          id
          name
          type
          mimeType
          url
          signedUrl
          thumbUrl
          title
          metadata
          streamUrl
          parentType
          isRead
          createdBy {
            id
            type
          }
        }
        isSharedWithMe(input: { academicYearId: $academicYearId })
        isDeleted
        entityMap {
          entityType
          courses @include(if: $getEntities) {
            id
            isArchived
            deletedBy {
              id
            }
          }
          grades @include(if: $getEntities) {
            id
          }
          curriculumPrograms @include(if: $getEntities) {
            id
          }
          users @include(if: $getEntities) {
            id
            ... on Student {
              isArchived
            }
            ... on Staff {
              isArchived
            }
            ... on FamilyMember {
              isArchived
            }
          }
        }
        coursesV2(input: { academicYearId: $academicYearId })
          @include(if: $isRecipientsVisible) {
          totalCount
        }
        sharedWith @include(if: $isRecipientsVisible)
        emailSharedWith @include(if: $isRecipientsVisible)
        recipients(input: { academicYearId: $academicYearId })
          @include(if: $isRecipientsVisible) {
          totalCount
          readByCount
        }
      }
    }
  }
  ${announcementFeedFragment}
`;

export const getAnnouncementRecipientsQuery = gql`
  query getAnnouncementRecipients($announcementId: ID!, $academicYearId: ID!) {
    node(id: $announcementId, type: CIRCULAR) {
      id
      ... on Circular {
        sharedWith
        recipients(input: { academicYearId: $academicYearId }) {
          totalCount
          readByCount
          edges {
            id
            isRead
            user {
              id
              firstName
              middleName
              lastName
              type
              profileImage
              email
              ... on Student {
                id
                prefix
                suffix
                pronouns
                preferredName
                alertTypes {
                  ...alertTypeFields
                }
              }
              ... on Staff {
                id
                prefix
                suffix
                pronouns
                preferredName
              }
              ... on FamilyMember {
                id
                prefix
                suffix
                pronouns
                preferredName
              }
            }
          }
        }
      }
    }
  }
  ${studentsAlertFragment}
`;

export const getAnnouncementCoursesV2Query = gql`
  query getAnnouncementV2Courses($announcementId: ID!, $academicYearId: ID!) {
    node(id: $announcementId, type: CIRCULAR) {
      id
      ... on Circular {
        coursesV2(input: { academicYearId: $academicYearId }) {
          totalCount
          edges {
            id
            title
            curriculumProgram {
              id
              acronym
            }
          }
        }
      }
    }
  }
`;

export const getSelectedTabAnnouncementsCountQuery = gql`
  query getSelectedTabAnnouncementsCount(
    $organizationId: ID!
    $createdAfter: String
    $createdBefore: String
    $academicYearId: ID
    $studentIds: [ID!]
    $states: [CIRCULAR_STATE_ENUM!]
    $curriculumProgramIds: [ID!]
    $isInboxTab: Boolean!
    $createdIn: CIRCULAR_ANNOUNCEMENT_TYPE_ENUM
    $courseIds: [ID!]
    $getToReviewCount: Boolean = false
    $createdBy: CIRCULAR_CREATED_BY_FILTER_ENUM = OTHERS
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        circularFeedV3 {
          selectedTabAnnouncementsCount: totalCount(
            input: {
              createdAfter: $createdAfter
              createdBefore: $createdBefore
              states: $states
              createdBy: $createdBy
              curriculumProgramIds: $curriculumProgramIds
              academicYearId: $academicYearId
              studentIds: $studentIds
              createdIn: $createdIn
              courseIds: $courseIds
            }
          )

          # Refetch the following unread count only if the user is on inbox tab
          # fetching this would implicitly update the count in cache and show
          # latest count on tab panel without us having to explicitly do it
          ...unreadAnnouncementsCountItem @include(if: $isInboxTab)
          toReviewCount: totalCount(
            input: {
              createdAfter: $createdAfter
              createdBefore: $createdBefore
              states: [REVIEW_PENDING]
              createdBy: OTHERS
              curriculumProgramIds: $curriculumProgramIds
              academicYearId: $academicYearId
              studentIds: $studentIds
            }
          ) @include(if: $getToReviewCount)
        }
      }
    }
  }
  ${unreadAnnouncementsCountFragment}
`;

// passing each variable differently as need to use some of the variables
// down in different places in query
export const getAnnouncementsFeedQuery = gql`
  query getAnnouncementsFeed(
    $organizationId: ID!
    $first: Int
    $after: String
    $orderBy: ORDERBY_FIELD_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $searchText: String
    $createdAfter: String
    $createdBefore: String
    $studentIds: [ID!]
    $states: [CIRCULAR_STATE_ENUM!]
    $sharedWith: [CIRCULAR_SHARED_WITH_ENUM!]
    $createdBy: CIRCULAR_CREATED_BY_FILTER_ENUM
    $createdByIds: [ID!]
    $curriculumProgramIds: [ID!]
    $academicYearId: ID!
    $getFilteredAnnouncementsCount: Boolean = false
    $isRead: Boolean
    $courseIds: [ID!]
    $isRecipientsVisible: Boolean = false
    $createdIn: CIRCULAR_ANNOUNCEMENT_TYPE_ENUM
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        circularFeedV3(
          input: {
            first: $first
            after: $after
            orderBy: $orderBy
            orderByDirection: $orderByDirection
            searchText: $searchText
            createdAfter: $createdAfter
            createdBefore: $createdBefore
            states: $states
            createdBy: $createdBy
            createdByIds: $createdByIds
            curriculumProgramIds: $curriculumProgramIds
            academicYearId: $academicYearId
            studentIds: $studentIds
            sharedWith: $sharedWith
            isRead: $isRead
            courseIds: $courseIds
            createdIn: $createdIn
          }
        ) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...announcementItem
              coursesV2(input: { academicYearId: $academicYearId })
                @include(if: $isRecipientsVisible) {
                totalCount
              }
              recipients(input: { academicYearId: $academicYearId })
                @include(if: $isRecipientsVisible) {
                totalCount
              }
              entityMap {
                entityType
              }
            }
          }

          # Only fetch count when in search screen, as in main route
          # we already fetching selected tabs count in an another query
          countForFilters: totalCount(
            input: {
              searchText: $searchText
              createdAfter: $createdAfter
              createdBefore: $createdBefore
              states: $states
              createdBy: $createdBy
              createdByIds: $createdByIds
              curriculumProgramIds: $curriculumProgramIds
              academicYearId: $academicYearId
              studentIds: $studentIds
              createdIn: $createdIn
              courseIds: $courseIds
              isRead: $isRead
              sharedWith: $sharedWith
            }
          ) @include(if: $getFilteredAnnouncementsCount)
        }
      }
    }
  }
  ${announcementFeedFragment}
`;

export const getUnreadAndNewAnnouncementsCountQuery = gql`
  query getUnreadAndNewAnnouncementsCount(
    $organizationId: ID!
    $createdAfter: String
    $createdBefore: String
    $curriculumProgramIds: [ID!]
    $academicYearId: ID
    $studentIds: [ID!]
    $createdIn: CIRCULAR_ANNOUNCEMENT_TYPE_ENUM
    $courseIds: [ID!]
    $getNewCount: Boolean = false
    $createdBy: CIRCULAR_CREATED_BY_FILTER_ENUM = OTHERS
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        circularFeedV3 {
          newAnnouncementsCount: totalCount(
            input: {
              createdBy: OTHERS
              states: [PUBLISHED]
              createdAfter: $createdAfter
              createdBefore: $createdBefore
              curriculumProgramIds: $curriculumProgramIds
              academicYearId: $academicYearId
              studentIds: $studentIds
              isNew: true
              createdIn: $createdIn
              courseIds: $courseIds
            }
          ) @include(if: $getNewCount)

          ...unreadAnnouncementsCountItem
        }
      }
    }
  }
  ${unreadAnnouncementsCountFragment}
`;

export const getAnnouncementsTabsCountQuery = gql`
  query getAnnouncementsTabsCount(
    $organizationId: ID!
    $createdAfter: String
    $createdBefore: String
    $curriculumProgramIds: [ID!]
    $academicYearId: ID
    $studentIds: [ID!]
    $createdIn: CIRCULAR_ANNOUNCEMENT_TYPE_ENUM
    $courseIds: [ID!]
    $createdBy: CIRCULAR_CREATED_BY_FILTER_ENUM = OTHERS
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        circularFeedV3 {
          ...unreadAnnouncementsCountItem

          totalDraftsCount: totalCount(
            input: {
              createdBy: ME
              states: [DRAFT]
              createdAfter: $createdAfter
              createdBefore: $createdBefore
              curriculumProgramIds: $curriculumProgramIds
              academicYearId: $academicYearId
              studentIds: $studentIds
              createdIn: $createdIn
              courseIds: $courseIds
            }
          )

          totalScheduledCount: totalCount(
            input: {
              createdBy: ME
              states: [SCHEDULED]
              createdAfter: $createdAfter
              createdBefore: $createdBefore
              curriculumProgramIds: $curriculumProgramIds
              academicYearId: $academicYearId
              studentIds: $studentIds
              createdIn: $createdIn
              courseIds: $courseIds
            }
          )

          # following total counts are prefetched initially just to know if we have any
          # items present, to show the correct loading screen for respective tabs
          totalSentCount: totalCount(
            input: {
              createdBy: ME
              states: [PUBLISHED]
              createdAfter: $createdAfter
              createdBefore: $createdBefore
              curriculumProgramIds: $curriculumProgramIds
              academicYearId: $academicYearId
              studentIds: $studentIds
              createdIn: $createdIn
              courseIds: $courseIds
            }
          )

          totalInboxCount: totalCount(
            input: {
              createdBy: OTHERS
              states: [PUBLISHED]
              createdAfter: $createdAfter
              createdBefore: $createdBefore
              curriculumProgramIds: $curriculumProgramIds
              academicYearId: $academicYearId
              studentIds: $studentIds
              createdIn: $createdIn
              courseIds: $courseIds
            }
          )
        }
      }
    }
  }
  ${unreadAnnouncementsCountFragment}
`;

export const getReviewTabsCountQuery = gql`
  query getReviewTabsCount(
    $organizationId: ID!
    $getToReviewCount: Boolean!
    $getUnderReviewCount: Boolean!
    $createdAfter: String
    $createdBefore: String
    $academicYearId: ID
    $studentIds: [ID!]
    $curriculumProgramIds: [ID!]
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        circularFeedV3 {
          underReviewCount: totalCount(
            input: {
              createdAfter: $createdAfter
              createdBefore: $createdBefore
              # the states order is important, helps in auto update of count
              # on fetch of current active tab count query
              states: [REVIEW_PENDING, REVIEW_REJECTED]
              createdBy: ME
              curriculumProgramIds: $curriculumProgramIds
              academicYearId: $academicYearId
              studentIds: $studentIds
            }
          ) @include(if: $getUnderReviewCount)

          toReviewCount: totalCount(
            input: {
              createdAfter: $createdAfter
              createdBefore: $createdBefore
              states: [REVIEW_PENDING]
              createdBy: OTHERS
              curriculumProgramIds: $curriculumProgramIds
              academicYearId: $academicYearId
              studentIds: $studentIds
            }
          ) @include(if: $getToReviewCount)
        }
      }
    }
  }
`;

export const getAnnouncementDetailsForEditQuery = gql`
  query getAnnouncementDetailsForEdit(
    $id: ID!
    $academicYearId: ID!
    $organizationId: ID!
  ) {
    node(id: $id, type: CIRCULAR) {
      id
      ... on Circular {
        ...announcementItem
        createdIn
        createdAt
        updatedAt
        isSharedWithMe(input: { academicYearId: $academicYearId })
        attachments {
          ...attachmentItem
        }
        aiConversation {
          edges {
            node {
              id
            }
          }
        }
        sharedWith
        emailSharedWith
        sendEmail
        entityMap {
          entityType
          courses {
            id
            title
            isArchived
            deletedBy {
              id
            }
          }
          grades {
            id
            name
          }
          curriculumPrograms {
            id
            acronym
            label
          }
          users {
            ...userDetailItem
            ... on Student {
              alertTypes {
                ...alertTypeFields
              }
            }
          }
        }
        coursesV2(input: { academicYearId: $academicYearId }) {
          totalCount
        }
        recipients(input: { academicYearId: $academicYearId }) {
          totalCount
        }
      }
    }
  }
  ${announcementFeedFragment}
  ${manageMembersFragments.basicUserItem}
  ${attachmentFragment.attachment}
  ${studentsAlertFragment}
`;

export const getUserCoursesQuery = gql`
  query getUserCourses(
    $id: ID!
    $filters: CourseFilter
    $courseGradeFilters: CourseGradeFilters
    $getGrades: Boolean!
    $getCurriculumProgram: Boolean!
    $userType: ENTITY_TYPE_ENUM!
    $getAssociatedProgram: Boolean = false
    $getLearningCourse: Boolean = false
  ) {
    node(id: $id, type: $userType) {
      id
      ... on Staff {
        id
        courses(orderBy: TITLE, orderByDirection: ASC, filters: $filters) {
          ...userCourseDetailsItem
        }
      }
      ... on Student {
        courses(orderBy: TITLE, orderByDirection: ASC, filters: $filters) {
          ...userCourseDetailsItem
        }
      }
    }
  }
  ${userCourseDetailsFragment}
`;

export const getUserCoursesV2Query = gql`
  query getUserCoursesV2(
    $id: ID!
    $filters: CourseFilter
    $userType: ENTITY_TYPE_ENUM!
    $getGrades: Boolean!
    $getCurriculumProgram: Boolean!
    $getAssociatedProgram: Boolean = false
    $getLearningCourse: Boolean = false
  ) {
    node(id: $id, type: $userType) {
      id
      ... on Staff {
        id
        coursesV2(orderBy: TITLE, orderByDirection: ASC, filters: $filters) {
          totalCount
          edges {
            node {
              id
              title
              isArchived
              deletedBy {
                id
              }
              grades @include(if: $getGrades) {
                id
                name
                curriculumProgram @include(if: $getAssociatedProgram) {
                  id
                  acronym
                }
              }
              curriculumProgram @include(if: $getCurriculumProgram) {
                id
                type
                label
                acronym
              }
              learningCourse @include(if: $getLearningCourse) {
                id
                title
                academicCourse {
                  id
                  label
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getUserCoursesV2PaginatedQuery = gql`
  query getUserCoursesV2Paginated(
    $id: ID!
    $first: Int
    $after: String
    $filters: CourseFilter
    $userType: ENTITY_TYPE_ENUM!
    $getGrades: Boolean!
    $getCurriculumProgram: Boolean!
    $getAssociatedProgram: Boolean = false
    $getLearningCourse: Boolean = false
    $getTotalCount: Boolean = false
  ) {
    node(id: $id, type: $userType) {
      id
      ... on Staff {
        id
        coursesV2(
          first: $first
          after: $after
          orderBy: TITLE
          orderByDirection: ASC
          filters: $filters
        ) {
          totalCount @include(if: $getTotalCount)
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              isArchived
              deletedBy {
                id
              }
              grades @include(if: $getGrades) {
                id
                name
                curriculumProgram @include(if: $getAssociatedProgram) {
                  id
                  label
                  acronym
                }
              }
              curriculumProgram @include(if: $getCurriculumProgram) {
                id
                type
                label
                acronym
              }
              learningCourse @include(if: $getLearningCourse) {
                id
                title
                academicCourse {
                  id
                  label
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getUserCountByEntityCourseWithEntityIds = gql`
  query userCountByEntityCourseWithEntityIds(
    $organizationId: ID!
    $input: UserCountByEntityInput!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        users {
          userCountByEntity(input: $input) {
            totalCount
            staffCount
            studentCount
            familyCount
            totalHasEmailCount
            staffHasEmailCount
            studentHasEmailCount
            familyHasEmailCount
          }
        }
      }
    }
  }
`;

export const getOrganizationCoursesQuery = gql`
  query getOrganizationCourses(
    $organizationId: ID!
    $courseFilters: CourseFilterInput
    $getGrades: Boolean!
    $getCurriculumProgram: Boolean!
    $getAssociatedProgram: Boolean = false
    $getLearningCourse: Boolean = false
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        courses(
          orderBy: TITLE
          orderByDirection: ASC
          filters: $courseFilters
        ) {
          edges {
            node {
              id
              title
              grades @include(if: $getGrades) {
                id
                name
                curriculumProgram @include(if: $getAssociatedProgram) {
                  id
                  acronym
                }
              }
              isArchived
              deletedBy {
                id
              }
              curriculumProgram @include(if: $getCurriculumProgram) {
                id
                type
                label
                acronym
              }
              learningCourse @include(if: $getLearningCourse) {
                id
                title
                academicCourse {
                  id
                  label
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationCoursesPaginatedQuery = gql`
  query getOrganizationCoursesPaginated(
    $organizationId: ID!
    $first: Int
    $after: String
    $courseFilters: CourseFilterInput
    $getGrades: Boolean!
    $getCurriculumProgram: Boolean!
    $getAssociatedProgram: Boolean = false
    $getLearningCourse: Boolean = false
    $getTotalCount: Boolean = false
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        courses(
          first: $first
          after: $after
          orderBy: TITLE
          orderByDirection: ASC
          filters: $courseFilters
        ) {
          totalCount @include(if: $getTotalCount)
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              grades @include(if: $getGrades) {
                id
                name
                curriculumProgram @include(if: $getAssociatedProgram) {
                  id
                  acronym
                }
              }
              isArchived
              deletedBy {
                id
              }
              curriculumProgram @include(if: $getCurriculumProgram) {
                id
                type
                label
                acronym
              }
              learningCourse @include(if: $getLearningCourse) {
                id
                title
                academicCourse {
                  id
                  label
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationGradesQuery = gql`
  query getOrganizationGrades(
    $organizationId: ID!
    $gradeFilters: GradeFilterInput
    $getCurriculumProgram: Boolean!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        grades(filters: $gradeFilters) {
          id
          name
          curriculumProgram @include(if: $getCurriculumProgram) {
            acronym
            label
            id
          }
        }
      }
    }
  }
`;

export const getOrganizationCoursesCountQuery = gql`
  query getOrganizationCoursesCount(
    $organizationId: ID!
    $filters: CourseFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        courses(filters: $filters) {
          totalCount
        }
      }
    }
  }
`;

// Manage members queries for Custom cohort
export const getGradeUsersQuery = gql`
  query getGradeUsers(
    $organizationId: ID!
    $filters: GradeFilterInput
    $skipStudents: Boolean!
    $skipStaff: Boolean!
    $skipFamilyMembers: Boolean!
    $allStudentsFilters: GradeAllStudentsFiltersInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        grades(filters: $filters) {
          id
          name
          allStaffs @skip(if: $skipStaff) {
            ...userDetailItem
          }
          allStudents(filters: $allStudentsFilters) @skip(if: $skipStudents) {
            ...userDetailItem
          }
          allParents @skip(if: $skipFamilyMembers) {
            ...userDetailItem
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getGradeUsersQueryV2 = gql`
  query getGradeUsersV2(
    $organizationId: ID!
    $first: Int
    $filters: OrganizationGradeV2FilterInput
    $allStudentsFilters: GradeAllStudentsFiltersInput
    $skipStudents: Boolean!
    $skipStaff: Boolean!
    $skipFamilyMembers: Boolean!
    $getTotalCount: Boolean = false
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        gradesV2(input: { first: $first, filter: $filters }) {
          totalCount(input: { filter: $filters }) @include(if: $getTotalCount)
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              name
              allStaffs @skip(if: $skipStaff) {
                ...userDetailItem
              }
              allStudents(filters: $allStudentsFilters)
                @skip(if: $skipStudents) {
                ...userDetailItem
              }
              allParents @skip(if: $skipFamilyMembers) {
                ...userDetailItem
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getYearGroupUsersQuery = gql`
  query getYearGroupUsers(
    $organizationId: ID!
    $filters: YearGroupFilter
    $skipStudents: Boolean!
    $skipStaff: Boolean!
    $skipFamilyMembers: Boolean!
    $allStudentsFilters: YearGroupAllStudentsFiltersInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        yearGroups(filters: $filters) {
          id
          name
          allStaffs @skip(if: $skipStaff) {
            ...userDetailItem
          }
          allStudents(filters: $allStudentsFilters) @skip(if: $skipStudents) {
            ...userDetailItem
          }
          allParents @skip(if: $skipFamilyMembers) {
            ...userDetailItem
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getStaffCourseUsersQuery = gql`
  query getCourseUsers(
    $id: ID!
    $first: Int
    $after: String
    $organizationId: ID!
    $filters: CourseFilter
    $skipStudents: Boolean!
    $skipStaff: Boolean!
    $skipFamilyMembers: Boolean!
    $studentFilters: CourseFilter
  ) {
    node(id: $id, type: STAFF) {
      ... on Staff {
        id
        coursesV2(first: $first, after: $after, filters: $filters) {
          pageInfo {
            hasNextPage
            endCursor
          }
          totalCount
          edges {
            node {
              id
              title
              students(filters: $studentFilters) @skip(if: $skipStudents) {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
              collaborator @skip(if: $skipStaff) {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
              familyMembers @skip(if: $skipFamilyMembers) {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getOrganizationCourseUsersQuery = gql`
  query getCourseUsers(
    $organizationId: ID!
    $first: Int
    $after: String
    $filters: CourseFilterInput
    $skipStudents: Boolean!
    $skipStaff: Boolean!
    $skipFamilyMembers: Boolean!
    $studentFilters: CourseFilter
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        courses(first: $first, after: $after, filters: $filters) {
          pageInfo {
            hasNextPage
            endCursor
          }
          totalCount
          edges {
            node {
              id
              title
              students(filters: $studentFilters) @skip(if: $skipStudents) {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
              collaborator @skip(if: $skipStaff) {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
              familyMembers @skip(if: $skipFamilyMembers) {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getCourseV2UsersQuery = gql`
  query getCourseV2Users(
    $organizationId: ID!
    $first: Int
    $after: String
    $filters: CourseFilterInput
    $skipStudents: Boolean!
    $skipStaff: Boolean!
    $skipFamilyMembers: Boolean!
    $studentFilters: CourseFilter
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        coursesV2(first: $first, after: $after, filters: $filters) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              students(filters: $studentFilters) @skip(if: $skipStudents) {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
              collaborator @skip(if: $skipStaff) {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
              familyMembers @skip(if: $skipFamilyMembers) {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getAnnouncementUsersPoolQuery = gql`
  query getAnnouncementUsersPool(
    $organizationId: ID!
    $getStudentFamilyMembers: Boolean!
    $getOnlyUserId: Boolean!
    $input: AnnouncementUserInput
    $includeCurriculumPrograms: Boolean = false
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        announcementUsers(input: $input) {
          pageInfo @skip(if: $getOnlyUserId) {
            hasNextPage
            endCursor
          }
          totalCount
          edges {
            node {
              id
              ...userItemV2 @skip(if: $getOnlyUserId)
              ... on Student {
                alertTypes @skip(if: $getOnlyUserId) {
                  ...alertTypeFields
                }
              }
            }
          }
        }
      }
    }
  }
  ${studentsAlertFragment}
  ${manageMembersFragments.userItemV2}
`;

export const getStaffCircularConfigQuery = gql`
  query getCircularConfig($id: ID!, $input: ShowReviewFeedTabsInput!) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        circularConfig {
          showReviewFeedTabs(input: $input) {
            toReview
            underReview
          }
        }
      }
    }
  }
`;

export const getUserCircularReviewersQuery = gql`
  query getUserCircularReviewers(
    $id: ID!
    $reviewerFilters: StaffCircularReviewersFilter!
  ) {
    node(id: $id, type: STAFF) {
      ... on Staff {
        id
        circularReviewers(filters: $reviewerFilters) {
          id
        }
      }
    }
  }
`;

export const getOrganizationConstantsQuery = gql`
  query getOrganizationConstants(
    $organizationId: ID!
    $filter: GlobalConstantsFilterOptions
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        globalConstants(filter: $filter) {
          id
          name
          value
        }
      }
    }
  }
`;

export const getPreviousAnnouncementRecipientsQuery = gql`
  query getAnnouncementsFeed(
    $organizationId: ID!
    $first: Int
    $after: String
    $orderBy: ORDERBY_FIELD_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $states: [CIRCULAR_STATE_ENUM!]
    $createdBy: CIRCULAR_CREATED_BY_FILTER_ENUM
    $curriculumProgramIds: [ID!]
    $academicYearId: ID!
    $createdIn: CIRCULAR_ANNOUNCEMENT_TYPE_ENUM
    $courseIds: [ID!]
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        circularFeedV3(
          input: {
            first: $first
            after: $after
            orderBy: $orderBy
            orderByDirection: $orderByDirection
            states: $states
            createdBy: $createdBy
            curriculumProgramIds: $curriculumProgramIds
            academicYearId: $academicYearId
            createdIn: $createdIn
            courseIds: $courseIds
          }
        ) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              publishedAt
              sharedWith
              emailSharedWith
              recipients(input: { academicYearId: $academicYearId }) {
                totalCount
              }
              entityMap {
                entityType
                courses {
                  id
                  title
                  isArchived
                  deletedBy {
                    id
                  }
                }
                grades {
                  id
                  name
                }
                curriculumPrograms {
                  id
                  acronym
                }
                users {
                  ...userDetailItem
                }
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getDefaultBannerImageQuery = gql`
  query getOrganizationGlobalConstants(
    $organizationId: ID!
    $filter: GlobalConstantsFilterOptions
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        globalConstants(filter: $filter) {
          id
          name
          value
        }
      }
    }
  }
`;

export const getUserLearningCourseClassesQuery = gql`
  query getUserLearningCourseClasses(
    $id: ID!
    $filters: CourseFilter
    $userType: ENTITY_TYPE_ENUM!
  ) {
    node(id: $id, type: $userType) {
      id
      ... on Staff {
        id
        coursesV2(orderBy: TITLE, orderByDirection: ASC, filters: $filters) {
          totalCount
          edges {
            node {
              id
              title
              isArchived
              deletedBy {
                id
              }
              grades {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;

export const getLearningCourseClassesUsersQuery = gql`
  query getLearningCourseClassesUsers(
    $classId: ID!
    $organizationId: ID!
    $studentFilters: CourseFilter
    $skipStudents: Boolean = true
    $skipFamilyMembers: Boolean = true
    $skipStaff: Boolean = true
  ) {
    node(id: $classId, type: COURSE) {
      id
      ... on Course {
        learningCourse {
          id
          classes {
            id
            title
            students(filters: $studentFilters) @skip(if: $skipStudents) {
              edges {
                node {
                  ...userDetailItem
                }
              }
            }
            collaborator @skip(if: $skipStaff) {
              edges {
                node {
                  ...userDetailItem
                }
              }
            }
            familyMembers @skip(if: $skipFamilyMembers) {
              edges {
                node {
                  ...userDetailItem
                }
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getUserLearningCourseClassesUsersQuery = gql`
  query getUserLearningCourseClassesUsers(
    $id: ID!
    $organizationId: ID!
    $filters: CourseFilter
    $userType: ENTITY_TYPE_ENUM!
    $skipStudents: Boolean = true
    $skipFamilyMembers: Boolean = true
    $skipStaff: Boolean = true
  ) {
    node(id: $id, type: $userType) {
      id
      ... on Staff {
        id
        coursesV2(orderBy: TITLE, orderByDirection: ASC, filters: $filters) {
          totalCount
          edges {
            node {
              id
              title
              isArchived
              deletedBy {
                id
              }
              curriculumProgram {
                id
                label
              }
              students @skip(if: $skipStudents) {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
              collaborator @skip(if: $skipStaff) {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
              familyMembers @skip(if: $skipFamilyMembers) {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getChildrenAcademicYearQuery = gql`
  query getChildrenAcademicYear($id: ID!, $type: ENTITY_TYPE_ENUM!) {
    node(id: $id, type: $type) {
      id
      ... on FamilyMember {
        children {
          id
          academicYears(filters: { archivalState: ACTIVE }) {
            ...academicYearItem
          }
        }
      }
    }
  }
  ${academicYearFragment.academicYear}
`;

export const getCourseUnreadAnnouncementsCountQuery = gql`
  query getCourseUnreadAnnouncementsCount(
    $id: ID!
    $unreadAnnouncementsCountInput: CircularFeedV3Input!
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        circularFeedV3 {
          ...unreadAnnouncementsCountItem
        }
      }
    }
  }
  ${unreadAnnouncementsCountCommonFragment}
`;

export const getLearningCourseUnreadAnnouncementsCountQuery = gql`
  query getLearningCourseUnreadAnnouncementsCount(
    $id: ID!
    $unreadAnnouncementsCountInput: CircularFeedV3Input!
  ) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        classes {
          id
          circularFeedV3 {
            ...unreadAnnouncementsCountItem
          }
        }
      }
    }
  }
  ${unreadAnnouncementsCountCommonFragment}
`;

export const getCourseUsersQueryV3 = gql`
  query getCourseUsersV3(
    $id: ID!
    $organizationId: ID!
    $first: Int
    $after: String
    $filters: CourseFilter
    $skipStudents: Boolean = true
    $skipStaff: Boolean = true
    $skipFamilyMembers: Boolean = true
    $includeCurriculumPrograms: Boolean = true
  ) {
    node(id: $id, type: STAFF) {
      ... on Staff {
        id
        coursesV2(first: $first, after: $after, filters: $filters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              grades {
                id
                name
              }
              curriculumProgram {
                id
                label
              }
              students @skip(if: $skipStudents) {
                edges {
                  node {
                    ...userDetailItemV2
                  }
                }
              }
              collaborator @skip(if: $skipStaff) {
                edges {
                  node {
                    ...userDetailItemV2
                  }
                }
              }
              familyMembers @skip(if: $skipFamilyMembers) {
                edges {
                  node {
                    ...userDetailItemV2
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItemV2}
`;

export const getOrganizationCourseUsersQueryV3 = gql`
  query getOrganizationCourseUsersV3(
    $organizationId: ID!
    $first: Int
    $after: String
    $filters: CourseFilterInput
    $skipStudents: Boolean = true
    $skipStaff: Boolean = true
    $skipFamilyMembers: Boolean = true
    $includeCurriculumPrograms: Boolean = true
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        courses(first: $first, after: $after, filters: $filters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              grades {
                id
                name
              }
              curriculumProgram {
                id
                label
              }
              students @skip(if: $skipStudents) {
                edges {
                  node {
                    ...userDetailItemV2
                  }
                }
              }
              collaborator @skip(if: $skipStaff) {
                edges {
                  node {
                    ...userDetailItemV2
                  }
                }
              }
              familyMembers @skip(if: $skipFamilyMembers) {
                edges {
                  node {
                    ...userDetailItemV2
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItemV2}
`;

export const getGradeUsersQueryV3 = gql`
  query getGradeUsersV3(
    $organizationId: ID!
    $first: Int
    $after: String
    $filters: OrganizationGradeV2FilterInput
    $skipStudents: Boolean = true
    $skipStaff: Boolean = true
    $skipFamilyMembers: Boolean = true
    $includeCurriculumPrograms: Boolean = true
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        gradesV2(input: { first: $first, after: $after, filter: $filters }) {
          totalCount(input: { filter: $filters })
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              name
              curriculumProgram {
                id
                label
              }
              allStaffs @skip(if: $skipStaff) {
                ...userDetailItemV2
              }
              allStudents @skip(if: $skipStudents) {
                ...userDetailItemV2
              }
              allParents @skip(if: $skipFamilyMembers) {
                ...userDetailItemV2
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItemV2}
`;
